import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, RefreshCw, Trash2, ChevronRight, TrendingDown, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

const fmtBDT = (n) => `৳ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function deficitSeverity(consecutive, amount) {
  if (consecutive >= 3 || amount >= 50000) return { color: "#ef4444", label: "CRITICAL", bg: "rgba(239,68,68,0.15)" };
  if (consecutive >= 2 || amount >= 10000) return { color: "#f59e0b", label: "WARNING", bg: "rgba(245,158,11,0.15)" };
  return { color: "#fcd34d", label: "WATCH", bg: "rgba(252,211,77,0.10)" };
}

export default function AffiliateDeficitTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(null);
  const [clearReason, setClearReason] = useState("");
  const [showClearFor, setShowClearFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/safe-cycle/deficits");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load deficits");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClear = async () => {
    if (!clearReason || clearReason.length < 3) return toast.error("Please enter a reason (min 3 chars)");
    if (!showClearFor) return;
    setClearing(showClearFor.account_id);
    try {
      const { data: res } = await api.post(`/admin/safe-cycle/deficits/${showClearFor.account_id}/clear`, { reason: clearReason });
      toast.success(`Deficit ${fmtBDT(res.old_deficit)} cleared for ${showClearFor.username}`);
      setShowClearFor(null);
      setClearReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear deficit");
    } finally { setClearing(null); }
  };

  if (loading) return <div className="text-white/60 p-6">Loading deficits…</div>;

  const rows = data?.rows || [];

  return (
    <div className="space-y-5" data-testid="page-deficit-tracker">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={18} style={{ color: "#ef4444" }} />
            <h1 className="text-[20px] font-black text-white">Cycle Deficit Tracker</h1>
          </div>
          <div className="text-[12px] text-white/55">
            Affiliates যাদের referred players নেট-উইন করেছে — তাদের commission deficit অবস্থায় আছে এবং পরের cycle এর কমিশন থেকে কাটা যাবে।
          </div>
        </div>
        <button onClick={load} className="ap-btn-ghost" data-testid="btn-refresh-deficits">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="ap-glass" style={{ padding: 18, border: "1px solid rgba(239,68,68,0.42)", background: "linear-gradient(135deg, rgba(239,68,68,0.14), transparent 70%)" }} data-testid="stat-total-deficit">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">Total Deficit (All Affiliates)</div>
            <TrendingDown size={14} style={{ color: "#ef4444" }} />
          </div>
          <div className="text-[24px] font-black mt-1" style={{ color: "#ef4444" }}>{fmtBDT(data?.total_deficit)}</div>
          <div className="text-[11px] text-white/45 mt-1">Carry-forward across cycles</div>
        </div>
        <div className="ap-glass" style={{ padding: 18 }} data-testid="stat-deficit-count">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">Affiliates in Deficit</div>
            <Users size={14} style={{ color: "#d4af37" }} />
          </div>
          <div className="text-[24px] font-black mt-1 text-white">{data?.count || 0}</div>
          <div className="text-[11px] text-white/45 mt-1">Currently active accounts</div>
        </div>
        <div className="ap-glass" style={{ padding: 18 }}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">Avg Deficit per Affiliate</div>
          <div className="text-[24px] font-black mt-1 text-white">{rows.length ? fmtBDT((data?.total_deficit || 0) / rows.length) : "—"}</div>
          <div className="text-[11px] text-white/45 mt-1">Total ÷ count</div>
        </div>
      </div>

      {/* Deficit list */}
      <div className="ap-glass" style={{ padding: 16 }}>
        <div className="text-white font-bold mb-3">Affiliates in Deficit</div>
        {rows.length === 0 ? (
          <div className="text-center py-8" data-testid="no-deficits">
            <div className="text-[36px] mb-2">✅</div>
            <div className="text-white font-bold mb-1">No affiliate is in deficit</div>
            <div className="text-[12px] text-white/55">সব affiliate এর under এ players নেট-লস এ আছে। Healthy!</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="deficits-table">
              <thead>
                <tr className="text-white/60">
                  <th className="text-left py-2 px-2">Affiliate</th>
                  <th className="text-right py-2 px-2">Players</th>
                  <th className="text-right py-2 px-2">Wallet</th>
                  <th className="text-right py-2 px-2">Deficit Amount</th>
                  <th className="text-center py-2 px-2">Consec Cycles</th>
                  <th className="text-center py-2 px-2">Severity</th>
                  <th className="text-center py-2 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sev = deficitSeverity(r.consecutive_deficit_cycles, r.commission_deficit);
                  return (
                    <tr key={r.account_id} className="border-t border-white/5">
                      <td className="py-2.5 px-2">
                        <div className="text-white font-bold">{r.username}</div>
                        <div className="text-[10px] text-white/45">{r.email}</div>
                      </td>
                      <td className="py-2.5 px-2 text-right text-white/80">{r.referred_players}</td>
                      <td className="py-2.5 px-2 text-right text-white/70">{fmtBDT(r.wallet_balance)}</td>
                      <td className="py-2.5 px-2 text-right">
                        <span className="font-black text-[15px]" style={{ color: "#ef4444" }}>{fmtBDT(r.commission_deficit)}</span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className="font-bold text-[14px] text-white">{r.consecutive_deficit_cycles}</span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.color}50` }}>
                          {sev.label}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <button
                          onClick={() => { setShowClearFor(r); setClearReason(""); }}
                          className="text-[11px] font-bold px-3 py-1 rounded-md inline-flex items-center gap-1"
                          style={{ background: "rgba(239,68,68,0.18)", color: "#fda5b1", border: "1px solid rgba(239,68,68,0.45)" }}
                          data-testid={`btn-clear-deficit-${r.account_id}`}
                        >
                          <Trash2 size={11} /> Clear
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info banner */}
      <div className="text-[11px] text-white/55 leading-relaxed p-3 rounded-lg" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.25)" }}>
        <strong style={{ color: "#d4af37" }}>ℹ️ Deficit কী?</strong> &nbsp;
        যখন একটা cycle এ affiliate এর referred players আপনার থেকে বেশি টাকা জিতে নেয় (net-win), তখন commission negative হয়। Negative amount কে &ldquo;Deficit&rdquo; বলে — সেটা পরের cycle এর positive commission থেকে কেটে যায়। Auto-cleared, না কাটলে carry forward থাকে।
        <br /><br />
        <strong style={{ color: "#d4af37" }}>Manual Clear:</strong> &nbsp;
        Exceptional cases এ (যেমন admin manual adjustment করেছেন বা business decision) &ldquo;Clear&rdquo; button দিয়ে deficit কে ০ করা যায়। প্রতিটা clear action audit log এ store হয় এবং reason mandatory।
      </div>

      {/* Clear confirmation modal */}
      {showClearFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }} onClick={() => setShowClearFor(null)} data-testid="clear-deficit-modal">
          <div className="bg-[#0a1018] border border-red-500/40 rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} style={{ color: "#ef4444" }} />
              <div className="text-white font-bold">Clear Deficit</div>
            </div>
            <div className="text-[12.5px] text-white/70 mb-3">
              <strong className="text-white">{showClearFor.username}</strong> এর deficit{" "}
              <span style={{ color: "#ef4444" }} className="font-bold">{fmtBDT(showClearFor.commission_deficit)}</span>{" "}
              ০ করা হবে। এটা reversible না — audit log এ permanent record থাকবে।
            </div>
            <textarea
              value={clearReason}
              onChange={(e) => setClearReason(e.target.value)}
              placeholder="Reason for clearing (min 3 chars) — required for audit log"
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#d4af37]"
              rows={3}
              maxLength={400}
              data-testid="input-clear-reason"
            />
            <div className="flex items-center gap-2 mt-4 justify-end">
              <button onClick={() => setShowClearFor(null)} className="ap-btn-ghost" data-testid="btn-cancel-clear">
                Cancel
              </button>
              <button
                onClick={handleClear}
                disabled={clearReason.length < 3 || clearing === showClearFor.account_id}
                className="px-4 py-2 rounded-lg font-bold text-[13px] inline-flex items-center gap-1"
                style={{ background: "rgba(239,68,68,0.25)", color: "#fda5b1", border: "1px solid rgba(239,68,68,0.5)", opacity: (clearReason.length < 3 || clearing) ? 0.5 : 1 }}
                data-testid="btn-confirm-clear"
              >
                <Trash2 size={12} /> {clearing === showClearFor.account_id ? "Clearing…" : "Confirm Clear"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
